import { useState } from 'react';
import { Column, ColumnFiltersState, SortingState, Table } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { ColumnFilterValue, isFilterValueActive } from '@/lib/table-query';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Label } from '@/components/ui-kit/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { emptyValue, FilterEditor, isMissingDays } from '../data-table/data-table-column-filter';
import { ColumnLabel } from './column-label';

const NO_SORT = 'none';

/** The columns the dialog offers: those with a `meta.filter`, and those that sort. */
export const filterableColumns = <TData,>(table: Table<TData>) =>
  table
    .getAllLeafColumns()
    .filter((column) => column.columnDef.meta?.filter && column.getCanFilter());

export const sortableColumns = <TData,>(table: Table<TData>) =>
  table.getAllLeafColumns().filter((column) => column.getCanSort());

interface DataCardFilterDialogProps<TData> {
  table: Table<TData>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The column filters and sorting of a table shown as cards (a phone has no column headers to hold
 * them), as the old portal's mobile filter panel: every filter in one dialog, applied together on
 * "Apply" in one table update; "Clear" removes them all.
 */
export const DataCardFilterDialog = <TData,>({
  table,
  open,
  onOpenChange,
}: Readonly<DataCardFilterDialogProps<TData>>) => {
  const { t } = useTranslation();
  const filterColumns = filterableColumns(table);
  const sortColumns = sortableColumns(table);

  const appliedDrafts = () =>
    Object.fromEntries(
      filterColumns.map((column) => [
        column.id,
        (column.getFilterValue() as ColumnFilterValue | undefined) ??
          emptyValue(column.columnDef.meta!.filter!),
      ])
    );
  const appliedSort = () => {
    const [first] = table.getState().sorting;
    return first ? `${first.id}:${first.desc ? 'desc' : 'asc'}` : NO_SORT;
  };

  const [drafts, setDrafts] = useState<Record<string, ColumnFilterValue>>(appliedDrafts);
  const [sort, setSort] = useState(appliedSort);
  const [wasOpen, setWasOpen] = useState(open);
  // Each opening starts from what is applied, not from a draft left unapplied last time.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDrafts(appliedDrafts());
      setSort(appliedSort());
    }
  }

  const blocked = filterColumns.some((column) =>
    isMissingDays(column.columnDef.meta!.filter!, drafts[column.id])
  );

  const apply = () => {
    if (blocked) return;
    const filters: ColumnFiltersState = filterColumns
      .filter((column) => isFilterValueActive(drafts[column.id]))
      .map((column) => ({ id: column.id, value: drafts[column.id] }));
    const [sortId, direction] = sort.split(':');
    const sorting: SortingState =
      sort === NO_SORT ? [] : [{ id: sortId, desc: direction === 'desc' }];
    table.setColumnFilters(filters);
    table.setSorting(sorting);
    onOpenChange(false);
  };

  const clear = () => {
    table.setColumnFilters([]);
    table.setSorting([]);
    onOpenChange(false);
  };

  const label = (column: Column<TData, unknown>) => <ColumnLabel table={table} column={column} />;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent alignCloseWithTitle>
        <DialogHeader>
          <DialogTitle>{t('FILTER')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          {sortColumns.length > 0 && (
            <div className="flex flex-col gap-2">
              <Label>{t('SORT_BY')}</Label>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_SORT}>{t('NONE')}</SelectItem>
                  {sortColumns.flatMap((column) => [
                    <SelectItem key={`${column.id}:asc`} value={`${column.id}:asc`}>
                      {label(column)} ({t('ASCENDING')})
                    </SelectItem>,
                    <SelectItem key={`${column.id}:desc`} value={`${column.id}:desc`}>
                      {label(column)} ({t('DESCENDING')})
                    </SelectItem>,
                  ])}
                </SelectContent>
              </Select>
            </div>
          )}

          {filterColumns.map((column) => {
            const config = column.columnDef.meta!.filter!;
            // Checkbox options sit beside their title; the other filters (a dropdown too) under it.
            const isInline = config.type === 'options' && !config.dropdown;
            return (
              <div
                key={column.id}
                className={
                  isInline ? 'flex flex-wrap items-center gap-x-4 gap-y-2' : 'flex flex-col gap-3'
                }
              >
                <Label className="whitespace-nowrap">
                  {label(column)}
                  {isInline && ':'}
                </Label>
                <FilterEditor
                  config={config}
                  draft={drafts[column.id]}
                  onDraftChange={(draft) => setDrafts((prev) => ({ ...prev, [column.id]: draft }))}
                  onSubmit={apply}
                  inline
                />
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={clear}>
            {t('CLEAR')}
          </Button>
          <Button type="button" onClick={apply} disabled={blocked}>
            {t('APPLY')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
